import React from 'react';
import { Box, Grid, makeStyles, Typography, useTheme } from '@material-ui/core';
import { IClientContract, IClientObjectModel } from 'interfaces';
import SensitiveValue from 'components/SensitiveValue';
import {
  DIVIDER_COLOR,
  Field,
  formatDateDots,
  formatDiscount,
  PILL_BLUE_BG,
  PILL_GREEN,
  PILL_GREY,
  PILL_YELLOW,
  Pill,
  SECONDARY_COLOR,
  SUBSTRATE_COLOR,
  useCardStyles,
} from './helpers';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${DIVIDER_COLOR}`,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  subtitle: {
    fontSize: 13,
    color: SECONDARY_COLOR,
    marginTop: 2,
  },
  headerPill: {
    marginLeft: 12,
    flexShrink: 0,
  },
  fields: {
    padding: '16px 20px',
  },
  tenantPhone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    margin: '0 20px 16px',
    padding: '8px 14px',
    borderRadius: 8,
    backgroundColor: SUBSTRATE_COLOR,
  },
  contracts: {
    padding: '12px 20px',
    borderTop: `1px solid ${DIVIDER_COLOR}`,
  },
  contractRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    fontSize: 14,
    marginTop: 8,
  },
  contractDate: {
    color: SECONDARY_COLOR,
  },
  contractPill: {
    marginLeft: 6,
  },
}));

// Ключ строки из идентифицирующих полей; индекс — только когда они все пустые.
const rowKey = (parts: Array<string | null>, index: number): string =>
  parts.filter(Boolean).join('-') || `empty-${index}`;

const contractKey = (contract: IClientContract, index: number): string =>
  rowKey([contract.dogovorGuid, contract.nomerDogovora, contract.dataDogovora], index);

export const objectKey = (object: IClientObjectModel, index: number): string =>
  rowKey([object.roomGuid, object.contract], index);

const ContractRow: React.FC<{ contract: IClientContract }> = ({ contract }) => {
  const classes = useStyles();
  const theme = useTheme();
  const cardClasses = useCardStyles();
  const date = formatDateDots(contract.dataDogovora);
  return (
    <Box className={classes.contractRow}>
      <span>
        <span className={cardClasses.fieldValue}>{contract.nomerDogovora || contract.dogovorGuid || '-'}</span>
        {date && <span className={classes.contractDate}> · от {date}</span>}
      </span>
      <span>
        {contract.aktivnayaZapis && <Pill className={classes.contractPill} label="Активная" colors={PILL_GREEN} />}
        {contract.zayavka && <Pill className={classes.contractPill} label="Заявка" colors={PILL_GREY} />}
        {contract.tsessiya && (
          <Pill
            className={classes.contractPill}
            label="Цессия"
            colors={{ bg: PILL_BLUE_BG, color: theme.palette.primary.main }}
          />
        )}
      </span>
    </Box>
  );
};

// Список договоров купли-продажи; вызывается только с непустым списком.
const ContractList: React.FC<{ contracts: IClientContract[] }> = ({ contracts }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  return (
    <Box className={classes.contracts}>
      <Typography className={cardClasses.fieldLabel}>Договоры купли-продажи</Typography>
      {contracts.map((contract, index) => (
        <ContractRow key={contractKey(contract, index)} contract={contract} />
      ))}
    </Box>
  );
};

interface IObjectCardProps {
  object: IClientObjectModel;
  contracts: IClientContract[];
  phone: string;
}

/** Карточка объекта клиента: помещение, ДКП, аренда и договоры купли-продажи по нему. */
const ClientObjectCard: React.FC<IObjectCardProps> = ({ object, contracts, phone }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  const title = [object.realEstateName, object.roomName].filter(Boolean).join(', ');
  const hasTenant = !!object.tenantName || !!object.tenantPhone;

  return (
    <Box className={cardClasses.card} mb={1.5}>
      <Box className={classes.header}>
        <Box minWidth={0}>
          <Typography className={classes.title}>{title || '-'}</Typography>
          {object.propertyType && <Typography className={classes.subtitle}>{object.propertyType}</Typography>}
        </Box>
        {object.tenantName && <Pill className={classes.headerPill} label="Сдаёт в аренду" colors={PILL_YELLOW} />}
      </Box>
      <Box className={classes.fields}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Field label="Договор" value={object.contract} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Дата ДКП" value={formatDateDots(object.dkpDate)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Скидка" value={formatDiscount(object)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Field label="Арендатор" value={object.tenantName} />
          </Grid>
        </Grid>
      </Box>
      {hasTenant && (
        <Box className={classes.tenantPhone}>
          <Typography className={cardClasses.fieldLabel}>Телефон арендатора</Typography>
          <Box className={cardClasses.fieldValue}>
            <SensitiveValue masked={object.tenantPhone} phone={phone} field="TENANT_PHONE" roomGuid={object.roomGuid} />
          </Box>
        </Box>
      )}
      {contracts.length > 0 && <ContractList contracts={contracts} />}
    </Box>
  );
};

// Договоры, не привязанные ни к одному объекту клиента.
export const OtherContractsCard: React.FC<{ contracts: IClientContract[] }> = ({ contracts }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  return (
    <Box className={cardClasses.card} mb={1.5}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Прочие договоры</Typography>
      </Box>
      <ContractList contracts={contracts} />
    </Box>
  );
};

export default ClientObjectCard;
